import { useEffect, useMemo, useState } from 'react';
import { useMask, format } from '@react-input/mask';
import { useForm } from 'react-hook-form';
import { mergeRefs } from 'react-merge-refs';
import { zodResolver } from '@hookform/resolvers/zod';
import { FORM_STATE_STORAGE_KEY } from 'shared/config';
import { documentNumberMaskOptions, documentSerialMaskOptions, innMaskOptions, phoneMaskOptions } from 'shared/lib';
import { DataFromEsia, FormValues, Retailer } from '../types';
import { useUpdateFromEsia } from '../hooks';
import { formSchema } from '../model';
import { InputField } from './input-field';
import { AgreementDrawer } from './agreement-drawer';
import { QuestionnaireFormHeader } from './questionnaire-form-header';
import { QuestionnaireFormFooter } from './questionnaire-form-footer';
import styles from './questionnaire-form.module.scss';

type QuestionnaireFormProps = {
  dataFromEsia?: DataFromEsia;
  retailer: Retailer;
  isLoading?: boolean;
  onSubmitAction: (values: FormValues) => void;
  onEsiaRedirectAction: () => void;
};

const DEFAULT_VALUES: FormValues = {
  lastName: '',
  firstName: '',
  middleNameField: {
    middleName: '',
    doesntExist: false,
  },
  birthDate: '',
  birthPlace: '',
  documentType: 'Паспорт',
  documentSerial: '',
  documentNumber: '',
  documentIssuedBy: '',
  documentIssueDate: '',
  addressReg: '',
  addressFactField: {
    addressFact: '',
    same: false,
  },
  INN: '',
  phoneMobile: '',
  personalDataConsent: false,
  isGosuslugi: false,
};

// Если перечисленные в массиве поля были загружены из госулуг, а потом изменены пользователем, то значение isGosuslugi становит false
const ESIA_SENSITIVE_FIELDS: (keyof FormValues)[] = [
  'lastName',
  'firstName',
  'middleNameField',
  'birthDate',
  'birthPlace',
  'documentSerial',
  'documentNumber',
  'documentIssuedBy',
  'documentIssueDate',
  'addressReg',
];

export const QuestionnaireForm = ({
  dataFromEsia,
  retailer,
  isLoading,
  onSubmitAction,
  onEsiaRedirectAction,
}: QuestionnaireFormProps) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const storedValues = useMemo<FormValues | undefined>(() => {
    const valuesFromLocalStorage: Partial<FormValues> = JSON.parse(
      localStorage.getItem(FORM_STATE_STORAGE_KEY) || '{}',
    );
    const { documentSerial, documentNumber, INN, phoneMobile } = valuesFromLocalStorage;

    return {
      ...DEFAULT_VALUES,
      ...valuesFromLocalStorage,
      documentSerial: documentSerial ? format(documentSerial, documentSerialMaskOptions) : '',
      documentNumber: documentNumber ? format(documentNumber, documentNumberMaskOptions) : '',
      INN: INN ? format(INN, innMaskOptions) : '',
      phoneMobile: phoneMobile ? format(phoneMobile.replace(/^((\+?7)|8)/, ''), phoneMaskOptions) : '',
    };
  }, []);

  const [isOpenAgreement, setIsOpenAgreement] = useState(false);
  const toggleAgreementDrawer = () => {
    setIsOpenAgreement((prevState) => !prevState);
  };

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isValid, touchedFields, dirtyFields },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onTouched',
    values: storedValues,
  });

  const values = watch();
  const middleNameDoesntExist = watch('middleNameField.doesntExist');
  const sameAddress = watch('addressFactField.same');
  const addressReg = watch('addressReg');

  const documentSerialMaskRef = useMask(documentSerialMaskOptions);
  const documentNumberMaskRef = useMask(documentNumberMaskOptions);
  const innMaskRef = useMask(innMaskOptions);
  const phoneMaskRef = useMask(phoneMaskOptions);

  const { ref: documentSerialInputRef, ...documentSerialControl } = register('documentSerial');
  const { ref: documentNumberInputRef, ...documentNumberControl } = register('documentNumber');
  const { ref: innInputRef, ...innControl } = register('INN');
  const { ref: phoneInputRef, ...phoneControl } = register('phoneMobile');

  useEffect(() => {
    localStorage.setItem(FORM_STATE_STORAGE_KEY, JSON.stringify(values));
  }, [values]);

  useUpdateFromEsia(setValue, dataFromEsia);

  useEffect(() => {
    if (middleNameDoesntExist) {
      setValue('middleNameField.middleName', '');
    }
  }, [middleNameDoesntExist, setValue]);

  useEffect(() => {
    if (sameAddress) {
      setValue('addressFactField.addressFact', addressReg);
    }
  }, [sameAddress, addressReg, setValue]);

  const handleChange = () => {
    if (
      ESIA_SENSITIVE_FIELDS.some((field) => dirtyFields[field] == true) ||
      dirtyFields.middleNameField?.middleName == true ||
      dirtyFields.middleNameField?.doesntExist == true
    ) {
      setValue('isGosuslugi', false);
    }
  };

  if (!isClient) {
    return null;
  }

  const hasTouchedFields = Object.keys(touchedFields).length > 0;

  const [today] = new Date().toISOString().split('T');
  const minBirthDate = '1900-01-01';

  return (
    <>
      <div className={styles.contentBlock}>
        <form
          onSubmit={handleSubmit(onSubmitAction)}
          className={styles.form}
          id="questionnaire-form"
          onChange={handleChange}
          noValidate
        >
          <QuestionnaireFormHeader onEsiaRedirectAction={onEsiaRedirectAction} isLoading={isLoading} />
          <div className={styles.inputs}>
            <InputField
              tabIndex={1}
              {...register('firstName')}
              label="Имя"
              placeholder="Константин"
              className={styles.dynamicInput}
              invalid={Boolean(errors.firstName)}
              helperText="Только кириллица, дефис и пробел"
            />
            <InputField
              tabIndex={2}
              {...register('lastName')}
              label="Фамилия"
              placeholder="Константинопольский"
              invalid={Boolean(errors.lastName)}
              helperText="Только кириллица, дефис и пробел"
            />
            <InputField
              tabIndex={3}
              {...register('middleNameField.middleName')}
              label="Отчество"
              disabled={middleNameDoesntExist}
              placeholder={middleNameDoesntExist ? '' : 'Владимирович'}
              invalid={Boolean(errors.middleNameField?.middleName)}
              helperText="Только кириллица, дефис и пробел"
            >
              <div className={styles.checkbox}>
                <input
                  tabIndex={4}
                  type="checkbox"
                  id="checkbox-middleName"
                  className={styles.box}
                  {...register('middleNameField.doesntExist')}
                />
                <label htmlFor="checkbox-middleName" className={styles.label}>
                  Нет отчества
                </label>
              </div>
            </InputField>
            <InputField
              tabIndex={5}
              {...register('birthDate')}
              type="date"
              min={minBirthDate}
              max={today}
              label="Дата рождения"
              className={styles.dynamicInput}
              placeholder="10.10.1990"
              invalid={Boolean(errors.birthDate)}
            />
            <InputField
              tabIndex={6}
              {...register('birthPlace')}
              label="Место рождения"
              placeholder="г. Смоленск"
              invalid={Boolean(errors.birthPlace)}
              helperText="Кириллица, цифры и знаки . , -"
            />
            <InputField
              tabIndex={7}
              {...register('documentType')}
              disabled
              placeholder="Паспорт"
              className={styles.dynamicInput}
              label="Документ удостоверящий личность"
              invalid={false}
            />
            <InputField
              tabIndex={8}
              {...documentSerialControl}
              label="Серия"
              ref={mergeRefs([documentSerialInputRef, documentSerialMaskRef])}
              placeholder="0000"
              className={styles.dynamicInput}
              invalid={Boolean(errors.documentSerial)}
            />
            <InputField
              tabIndex={9}
              {...documentNumberControl}
              label="Номер"
              ref={mergeRefs([documentNumberInputRef, documentNumberMaskRef])}
              className={styles.dynamicInput}
              placeholder="111 222"
              invalid={Boolean(errors.documentNumber)}
            />
            <InputField
              tabIndex={10}
              {...register('documentIssuedBy')}
              label="Кем выдан"
              placeholder="ОУФМС РОССИИ в Смоленской области"
              invalid={Boolean(errors.documentIssuedBy)}
              helperText="Кириллица, цифры и знаки . , - №"
            />
            <InputField
              tabIndex={11}
              {...register('documentIssueDate')}
              type="date"
              label="Когда выдан"
              placeholder="11.11.2004"
              min="1997-01-01"
              max={today}
              className={styles.dynamicInput}
              invalid={Boolean(errors.documentIssueDate)}
            />
            <InputField
              tabIndex={12}
              {...register('addressReg')}
              label="Адрес постоянной регистрации"
              placeholder="г. Москва, ул. Тверская, д. 1, кв. 1"
              invalid={Boolean(errors.addressReg)}
              helperText="Кириллица, цифры и знаки . , - /"
            />
            <InputField
              tabIndex={13}
              {...register('addressFactField.addressFact')}
              label="Адрес фактического места проживания"
              disabled={sameAddress}
              placeholder="г. Москва, ул. Тверская, д. 1, кв. 1"
              invalid={Boolean(errors.addressFactField?.addressFact)}
              helperText="Кириллица, цифры и знаки . , - /"
            >
              <div className={styles.checkbox}>
                <input
                  tabIndex={14}
                  type="checkbox"
                  id="checkbox-addressFact"
                  className={styles.box}
                  {...register('addressFactField.same')}
                />
                <label htmlFor="checkbox-addressFact" className={styles.label}>
                  Совпадает с адресом регистрации
                </label>
              </div>
            </InputField>
            <InputField
              tabIndex={15}
              {...innControl}
              label="ИНН"
              ref={mergeRefs([innInputRef, innMaskRef])}
              placeholder="11244803058"
              className={styles.dynamicInput}
              invalid={Boolean(errors.INN)}
            />
            <InputField
              tabIndex={16}
              {...phoneControl}
              label="Мобильный телефон"
              ref={mergeRefs([phoneInputRef, phoneMaskRef])}
              placeholder="+7 000 000 00 00"
              className={styles.dynamicInput}
              invalid={Boolean(errors.phoneMobile)}
            />
          </div>
          <QuestionnaireFormFooter
            error={hasTouchedFields || (hasTouchedFields && !isValid)}
            register={register}
            toggleAgreementDrawer={toggleAgreementDrawer}
          />
        </form>
      </div>
      <AgreementDrawer open={isOpenAgreement} retailer={retailer} onClose={toggleAgreementDrawer} />
    </>
  );
};
